import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaStar,
  FaRocket,
  FaEye,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const HighQualityVideoEditingInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="High-Quality Video Editing in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Transform Your Footage</Subheading>}
        heading={
          <Heading>
            High-Quality <span tw="text-primary-500">Video Editing</span> in
            Dubai
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we provide high-quality video editing services in
            **Dubai**, turning raw footage into polished, engaging content.
            Using advanced tools like Adobe Premiere Pro and DaVinci Resolve, we
            craft cinematic videos for corporate, promotional, and social media
            needs.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1601506521937-0b4218f2e801?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Film production setup
          "https://images.unsplash.com/photo-1540655037529-dec987208707?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Creative lighting effects
          "https://images.unsplash.com/photo-1580983218765-f663bec07b37?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Professional camera equipment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Editing Now"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Premier Video Editing Services in Dubai"
        description="We deliver polished videos with precision and creativity."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Cinematic Polish",
            description: "4K editing with professional color grading.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Expert Editors",
            description: "Skilled team using industry-standard tools.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Turnaround",
            description: "Quick delivery for tight deadlines.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Engaging Storytelling",
            description: "Edits that captivate and retain viewers.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Client-Focused",
            description: "Tailored edits to match your vision.",
          },
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Versatile Expertise",
            description: "Corporate, events, and social media content.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our High-Quality Editing Process"
        description={
          <Description>
            We transform your footage with a streamlined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>Consultation: Understand your vision and goals.</li>
              <li>Review: Analyze raw footage for editing potential.</li>
              <li>Editing: Apply cuts, transitions, and effects.</li>
              <li>
                Enhancement: Add color grading, audio, and motion graphics.
              </li>
              <li>
                Delivery: Provide polished videos in your preferred format.
              </li>
              <li>Revisions: Offer unlimited revisions for satisfaction.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1610374792793-f016b77ca51a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Studio production setup
          "https://images.unsplash.com/photo-1536240478700-b869070f9279?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Artistic video composition
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Video Editing Wins in Dubai"
        description="Our edits have elevated brands and events."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Promo Impact",
            description: "Enhanced a promotional video for 2x engagement.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Brand Reach",
            description: "Boosted visibility for a Dubai startup.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Praise",
            description: "96% satisfaction from diverse clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Polished Videos?</Subheading>}
        heading="High-Quality Video Editing in Dubai"
        description={
          <Description>
            Partner with MilaajBrandset for high-quality video editing services
            in Dubai. Get cinematic, engaging content that reflects your
            brand—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1536240478700-b869070f9279?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Video editing workspace
          "https://images.unsplash.com/photo-1546412414-e1885259563a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Dubai skyline
          "https://images.unsplash.com/photo-1523207911345-32501502db22?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Professional camera equipment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
